import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { chatService, folderService, llmConfigService } from '../../services/api';
import ChatHistorySidebar from './ChatHistorySidebar';

const CrossProjectTab = () => {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [exportingMessageId, setExportingMessageId] = useState(null);
  const [exportedDocument, setExportedDocument] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGoogleDocsPermissionModal, setShowGoogleDocsPermissionModal] = useState(false);
  const [googleDocsAuthUrl, setGoogleDocsAuthUrl] = useState('');
  
  // Model settings
  const [availableModels, setAvailableModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  
  // Filter states
  const [allFolders, setAllFolders] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [folderDescendants, setFolderDescendants] = useState({}); // Maps folder ID to array of descendant IDs
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  
  const chatContainerRef = useRef(null);
  const filtersRef = useRef(null);

  // Load conversations, folders, and models on mount
  useEffect(() => {
    loadConversations();
    loadFoldersAndProjects();
    loadModelSettings();
  }, []);

  // Click outside to collapse filters
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target) && isFiltersExpanded) {
        setIsFiltersExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFiltersExpanded]);

  // Load folders and projects for filters
  const loadFoldersAndProjects = async () => {
    try {
      setIsLoadingFilters(true);
      const response = await folderService.getFoldersAndProjects();
      
      // Flatten folder structure for dropdown
      const folders = [];
      const projects = [];
      const folderDescendants = {}; // Track all descendant folder IDs
      
      const flattenFolder = (folder, path = '', depth = 0, parentId = null) => {
        const currentPath = path ? `${path} / ${folder.name}` : folder.name;
        folders.push({ 
          id: folder.id, 
          name: folder.name, 
          path: currentPath,
          depth,
          parentId
        });
        
        // Initialize descendants list for this folder
        if (!folderDescendants[folder.id]) {
          folderDescendants[folder.id] = [folder.id];
        }
        
        // Add projects from this folder
        if (folder.projects) {
          folder.projects.forEach(project => {
            projects.push({ ...project, folderPath: currentPath, folderId: folder.id });
          });
        }
        
        // Recursively flatten children
        if (folder.children) {
          folder.children.forEach(child => {
            flattenFolder(child, currentPath, depth + 1, folder.id);
            // Add child's ID and all its descendants to parent's descendants
            folderDescendants[folder.id].push(child.id);
            if (folderDescendants[child.id]) {
              folderDescendants[folder.id].push(...folderDescendants[child.id]);
            }
          });
        }
      };
      
      // Process shared folders
      if (response.data.shared) {
        response.data.shared.forEach(folder => flattenFolder(folder));
      }
      
      // Process personal folders
      if (response.data.personal) {
        response.data.personal.forEach(folder => flattenFolder(folder, 'Personal'));
      }
      
      // Add orphaned projects
      if (response.data.orphanedShared) {
        response.data.orphanedShared.forEach(project => {
          projects.push({ ...project, folderPath: 'No Folder', folderId: null });
        });
      }
      
      setAllFolders(folders);
      setAllProjects(projects);
      setFolderDescendants(folderDescendants);
      
      // Select all projects by default
      const allProjectIds = projects.map(p => p.id);
      setSelectedProjectIds(allProjectIds);
    } catch (err) {
      console.error('Error loading folders and projects:', err);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  // Load conversations list
  const loadConversations = async () => {
    try {
      setIsLoadingConversations(true);
      const response = await chatService.getConversations(null, true); // isCrossProject = true
      setConversations(response.data || []);
      
      // Set isLoading to false on initial load if no conversation is selected
      if (!currentConversationId) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      setIsLoading(false); // Also set to false on error
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      setCurrentConversationId(conversationId);
      const response = await chatService.getConversationMessages(conversationId);
      setMessages(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new conversation
  const handleNewChat = async () => {
    setCurrentConversationId(null);
    setMessages([]);
    setMetadata(null);
    setError(null);
    setIsLoading(false); // Ensure loading overlay is hidden for new chat
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId);
      
      // If we deleted the current conversation, clear it
      if (conversationId === currentConversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      
      // Refresh conversations list
      await loadConversations();
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  // Load available models and current preference
  const loadModelSettings = async () => {
    try {
      const [modelsRes, prefRes] = await Promise.all([
        llmConfigService.getAvailableModels(),
        llmConfigService.getPreference(null) // Global preference for cross-project
      ]);
      
      setAvailableModels(modelsRes.data || []);
      setCurrentModel(prefRes.data || null);
    } catch (err) {
      console.error('Error loading model settings:', err);
    }
  };

  // Handle model change
  const handleModelChange = async (modelName) => {
    try {
      await llmConfigService.setPreference(modelName, currentModel?.temperature || 0.2);
      const preferenceRes = await llmConfigService.getPreference();
      setCurrentModel(preferenceRes.data);
    } catch (err) {
      console.error('Error changing model:', err);
      setError('Failed to change model');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Strip markdown formatting from text
  const stripMarkdown = (text) => {
    let cleaned = text;
    
    // Remove bold (**text** or __text__)
    cleaned = cleaned.replace(/\*\*([^*]+?)\*\*/g, '$1');
    cleaned = cleaned.replace(/__([^_]+?)__/g, '$1');
    
    // Remove italic (*text* or _text_)
    cleaned = cleaned.replace(/\*([^*]+?)\*/g, '$1');
    cleaned = cleaned.replace(/_([^_]+?)_/g, '$1');
    
    // Remove code blocks (```code```)
    cleaned = cleaned.replace(/```[\s\S]*?```/g, (match) => {
      return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
    });
    
    // Remove inline code (`code`)
    cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
    
    // Remove headers (# ## ###)
    cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
    
    // Remove links [text](url)
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove images ![alt](url)
    cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
    
    return cleaned;
  };

  // Copy message to clipboard
  const handleCopyMessage = async (messageId, content) => {
    try {
      const plainText = stripMarkdown(content);
      await navigator.clipboard.writeText(plainText);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy message');
    }
  };

  // Export message to Google Docs
  const handleExportToGoogleDocs = async (messageId, content) => {
    try {
      setExportingMessageId(messageId);
      const response = await chatService.exportMessageToGoogleDocs(messageId, content, 'Cross-Project Analysis');
      
      if (response.data && response.data.documentUrl) {
        const { documentUrl, title } = response.data;
        setExportedDocument({ url: documentUrl, title });
        setShowExportModal(true);
      }
    } catch (err) {
      console.error('Error exporting to Google Docs:', err);
      
      // Check if we need to request Google Docs permissions
      if (err.response?.data?.needsAuth) {
        const currentPath = window.location.pathname;
        const authUrl = `/api/google-docs/auth?returnTo=${encodeURIComponent(currentPath)}`;
        setGoogleDocsAuthUrl(authUrl);
        setShowGoogleDocsPermissionModal(true);
        return;
      }
      
      let errorMessage = 'Failed to export to Google Docs. ';
      if (err.response?.status === 401) {
        errorMessage += 'Please log in with Google to use this feature.';
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      }
      alert(errorMessage);
    } finally {
      setExportingMessageId(null);
    }
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Get filtered projects based on selected folder (includes all nested projects)
  const getFilteredProjects = () => {
    if (!selectedFolderId) {
      return allProjects;
    }
    // Get all descendant folder IDs (including the selected folder itself)
    const descendantIds = folderDescendants[selectedFolderId] || [selectedFolderId];
    // Return projects that belong to any of these folders
    return allProjects.filter(p => descendantIds.includes(p.folderId));
  };

  // Handle folder selection
  const handleFolderChange = (e) => {
    const folderId = e.target.value || null;
    setSelectedFolderId(folderId);
    
    // When a folder is selected, automatically select all its child projects
    if (folderId) {
      const descendantIds = folderDescendants[folderId] || [folderId];
      const childProjects = allProjects.filter(p => descendantIds.includes(p.folderId));
      const childProjectIds = childProjects.map(p => p.id);
      setSelectedProjectIds(childProjectIds);
    } else {
      // If "All Folders" is selected, select all projects
      const allProjectIds = allProjects.map(p => p.id);
      setSelectedProjectIds(allProjectIds);
    }
  };

  // Handle project selection (multi-select)
  const handleProjectToggle = (projectId) => {
    setSelectedProjectIds(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedFolderId(null);
    // Select all projects when resetting
    const allProjectIds = allProjects.map(p => p.id);
    setSelectedProjectIds(allProjectIds);
  };

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isTyping) return;

    // Clear input and error immediately
    setInputMessage('');
    setIsTyping(true);
    setError(null);

    // Add user message optimistically
    const tempUserMsg = {
      id: `temp-user-${Date.now()}`,
      message,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await chatService.sendMessage(
        null, // no specific projectId for cross-project
        message, // Send the original message without prefix
        currentConversationId,
        true, // isCrossProject = true
        null, // processId
        selectedProjectIds // Pass selected project IDs as parameter
      );

      // If this was a new conversation, update the conversationId
      if (!currentConversationId && response.data.conversationId) {
        setCurrentConversationId(response.data.conversationId);
        // Add the new conversation to the list
        setConversations(prev => [{
          id: response.data.conversationId,
          projectId: null,
          displayTitle: message.length > 50 ? message.substring(0, 47) + '...' : message,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isCrossProject: true,
          _count: { messages: 2 }
        }, ...prev]);
      }

      // Replace temp message with actual server response
      setMessages(prev => {
        // Remove temp message and add both user and assistant messages from server
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [...withoutTemp, response.data.userMessage, response.data.assistantMessage];
      });

      // Store metadata for display (optional)
      if (response.data.metadata) {
        setMetadata(response.data.metadata);
      }
      
      // Update the conversation in the list (update timestamp)
      if (response.data.conversationId) {
        setConversations(prev => 
          prev.map(c => 
            c.id === response.data.conversationId 
              ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
              : c
          )
        );
      }
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Determine user-friendly error message
      let userErrorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (err.response?.status === 503) {
        userErrorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.status === 504) {
        userErrorMessage = 'The request timed out. Please try a simpler question or try again later.';
      } else if (err.response?.status === 500) {
        userErrorMessage = 'An internal server error occurred. Please try again later.';
      } else if (err.response?.data?.error) {
        userErrorMessage = err.response.data.error;
      } else if (err.message?.includes('Network Error')) {
        userErrorMessage = 'Network connection lost. Please check your internet connection and try again.';
      }
      
      // Show error message in chat
      const errorMsg = {
        id: `error-${Date.now()}`,
        message: `❌ **Error**: ${userErrorMessage}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      // Remove temp message and add error
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [...withoutTemp, errorMsg];
      });
      
      setError(userErrorMessage);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return !inline && language ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="rounded-lg my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  const suggestedQuestions = [
    "What common patterns exist across my projects?",
    "Which projects implement error handling best?",
    "Show me all projects using Excel activities",
    "Compare login workflows across projects",
    "What workflows could be shared?"
  ];

  const filteredProjects = getFilteredProjects();

  return (
    <div className="flex h-full gap-4">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col rounded-none border-0 border-r border-gray-200 bg-white overflow-hidden px-4 pt-4 pb-4 relative">
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
            <div className="text-gray-500">Loading...</div>
          </div>
        )}
        
        {/* Filters Section */}
        <div ref={filtersRef} className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200">
          <button
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            className="flex items-center justify-between w-full text-left font-semibold text-gray-800 hover:text-blue-700 transition-colors p-4"
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-gray-800">Filters</span>
              {!isFiltersExpanded && selectedProjectIds.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
                  {selectedProjectIds.length === allProjects.length 
                    ? 'All projects selected'
                    : `${selectedProjectIds.length} project${selectedProjectIds.length !== 1 ? 's' : ''} selected`
                  }
                </span>
              )}
            </span>
              <svg 
                className={`w-5 h-5 text-blue-600 transition-transform ${isFiltersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isFiltersExpanded && (
              <div className="px-4 pb-4 space-y-4">
                {/* Folder Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Folder
                  </label>
                  <select
                    value={selectedFolderId || ''}
                    onChange={handleFolderChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                    disabled={isLoadingFilters}
                  >
                    <option value="">📁 All Folders</option>
                    {allFolders.map(folder => {
                      // Create visual indentation based on depth
                      const indent = '\u00A0\u00A0\u00A0'.repeat(folder.depth); // Non-breaking spaces
                      const prefix = folder.depth === 0 ? '📁 ' : '└─ ';
                      return (
                        <option key={folder.id} value={folder.id}>
                          {indent}{prefix}{folder.name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Projects ({selectedProjectIds.length} selected)
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white shadow-inner">
                    {filteredProjects.length === 0 ? (
                      <div className="text-sm text-gray-500 text-center py-4">
                        {isLoadingFilters ? 'Loading projects...' : 'No projects available'}
                      </div>
                    ) : (
                      filteredProjects.map(project => (
                        <label
                          key={project.id}
                          className="flex items-start gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProjectIds.includes(project.id)}
                            onChange={() => handleProjectToggle(project.id)}
                            className="mt-1 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {project.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {project.folderPath}
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Reset Button */}
                <button
                  onClick={handleResetFilters}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors shadow-sm"
                  disabled={!selectedFolderId && selectedProjectIds.length === allProjects.length}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Scrollable Chat Messages Area */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto mb-4">
            <div className="flex flex-col space-y-3 px-2">
              {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center px-4">
                <div className="max-w-2xl">
                  <p className="text-gray-700 font-medium mb-2">
                    Cross-Project Analysis
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Ask questions that span multiple projects or analyze patterns across your workspace
                  </p>
                  
                  {/* Suggested Questions */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-3">Try asking:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedQuestions.map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => setInputMessage(question)}
                          className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 ${
                      msg.sender === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    {(msg.sender === 'agent' || msg.sender === 'assistant') ? (
                      <div className="chat-markdown">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {msg.message}
                        </ReactMarkdown>
                        
                        {/* Display LLM Execution Metrics and Action Buttons */}
                        {msg.llmExecution && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between gap-4">
                              {/* Left side: Metrics */}
                              <div className="flex items-center gap-3 text-xs text-gray-600">
                                {msg.timestamp && (
                                  <span className="text-gray-500">
                                    {formatTimestamp(msg.timestamp)}
                                  </span>
                                )}
                                <span className="font-medium text-gray-700">
                                  🤖 {msg.llmExecution.displayName}
                                </span>
                                <span>
                                  📊 {msg.llmExecution.inputTokens.toLocaleString()} in / {msg.llmExecution.outputTokens.toLocaleString()} out
                                </span>
                                <span className="text-blue-600">
                                  ⏱️ {(msg.llmExecution.durationMs / 1000).toFixed(2)}s
                                </span>
                                <span className="font-medium text-green-700">
                                  💰 ${Math.ceil(parseFloat(msg.llmExecution.estimatedCost) * 1000) / 1000}
                                  {(() => {
                                    // Calculate cumulative cost up to and including this message
                                    const messageIndex = messages.findIndex(m => m.id === msg.id);
                                    const cumulativeCost = messages
                                      .slice(0, messageIndex + 1)
                                      .filter(m => m.llmExecution && m.llmExecution.estimatedCost)
                                      .reduce((sum, m) => sum + parseFloat(m.llmExecution.estimatedCost), 0);
                                    return ` (total: $${cumulativeCost.toFixed(3)})`;
                                  })()}
                                </span>
                              </div>
                              
                              {/* Right side: Action buttons */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleCopyMessage(msg.id, msg.message)}
                                  className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50"
                                  title={copiedMessageId === msg.id ? 'Copied!' : 'Copy message'}
                                >
                                  {copiedMessageId === msg.id ? (
                                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleExportToGoogleDocs(msg.id, msg.message)}
                                  disabled={exportingMessageId === msg.id}
                                  className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Export to Google Docs"
                                >
                                  {exportingMessageId === msg.id ? (
                                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                      <path d="M14 2v6h6" fill="#A1C2FA"/>
                                      <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action Icons for messages without metrics */}
                        {!msg.llmExecution && (
                          <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleCopyMessage(msg.id, msg.message)}
                              className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50"
                              title={copiedMessageId === msg.id ? 'Copied!' : 'Copy message'}
                            >
                              {copiedMessageId === msg.id ? (
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleExportToGoogleDocs(msg.id, msg.message)}
                              disabled={exportingMessageId === msg.id}
                              className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Export to Google Docs"
                            >
                              {exportingMessageId === msg.id ? (
                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                  <path d="M14 2v6h6" fill="#A1C2FA"/>
                                  <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                </svg>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      msg.message
                    )}
                    {msg.timestamp && msg.sender === 'user' && (
                      <div className="text-xs mt-2 text-blue-100">
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Fixed Bottom Section */}
        <div className="flex-shrink-0">
            {/* Suggested Questions - Show when no messages and not typing */}
            {messages.length > 0 && messages.length <= 1 && !isTyping && (
              <div className="mb-4 px-2">
                <p className="text-sm text-gray-600 mb-2">Try asking:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputMessage(question)}
                      className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Disclaimer */}
            <div className="mb-4 px-2">
              <p className="text-xs text-gray-500 text-center italic">
                ⚠️ Responses are AI-generated and may contain errors. Use as reference only.
              </p>
            </div>

            {/* Input Box */}
            <div className="px-2">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    selectedProjectIds.length > 0
                      ? `Ask about ${selectedProjectIds.length} selected project${selectedProjectIds.length > 1 ? 's' : ''}...`
                      : "Ask a question across all projects..."
                  }
                  className="flex-grow rounded-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isTyping}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  className="btn-primary px-6 py-2"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Chat History Sidebar */}
      <div className="w-80 h-full">
        <ChatHistorySidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={loadConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          onAnalyzeCode={null} // Not applicable for cross-project
          isLoading={isLoadingConversations}
          availableModels={availableModels}
          currentModel={currentModel}
          onModelChange={handleModelChange}
        />
      </div>
    </div>
  );
};

export default CrossProjectTab;